import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message, Alert, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  UserOutlined, MedicineBoxOutlined, FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { villageApi, VillageAkim, VetService, AgroInfo } from '../../api/village';

const { Title, Text } = Typography;
const { TextArea } = Input;

const VillagePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('akims');

  // Akims
  const [akims, setAkims] = useState<VillageAkim[]>([]);
  const [akimsLoading, setAkimsLoading] = useState(false);

  // Vet Services
  const [vetServices, setVetServices] = useState<VetService[]>([]);
  const [vetLoading, setVetLoading] = useState(false);

  // Agro Info
  const [agroInfo, setAgroInfo] = useState<AgroInfo[]>([]);
  const [agroLoading, setAgroLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Modals
  const [akimModalOpen, setAkimModalOpen] = useState(false);
  const [editingAkim, setEditingAkim] = useState<VillageAkim | null>(null);
  const [akimForm] = Form.useForm();

  const [vetModalOpen, setVetModalOpen] = useState(false);
  const [editingVet, setEditingVet] = useState<VetService | null>(null);
  const [vetForm] = Form.useForm();

  const [agroModalOpen, setAgroModalOpen] = useState(false);
  const [editingAgro, setEditingAgro] = useState<AgroInfo | null>(null);
  const [agroForm] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchAkims = useCallback(async () => {
    setAkimsLoading(true);
    try {
      const res = await villageApi.listAkims();
      setAkims(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setAkimsLoading(false);
    }
  }, []);

  const fetchVetServices = useCallback(async () => {
    setVetLoading(true);
    try {
      const res = await villageApi.listVetServices();
      setVetServices(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setVetLoading(false);
    }
  }, []);

  const fetchAgroInfo = useCallback(async () => {
    setAgroLoading(true);
    try {
      const res = await villageApi.listAgroInfo();
      setAgroInfo(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setAgroLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAkims();
    fetchVetServices();
    fetchAgroInfo();
  }, [fetchAkims, fetchVetServices, fetchAgroInfo]);

  // Akim handlers
  const handleAkimSubmit = async () => {
    let values: any;
    try { values = await akimForm.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      if (editingAkim) {
        await villageApi.updateAkim(editingAkim.id, values);
        messageApi.success('Данные обновлены');
      } else {
        await villageApi.createAkim(values);
        messageApi.success('Аким добавлен');
      }
      setAkimModalOpen(false);
      fetchAkims();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAkim = async (id: number) => {
    try { await villageApi.deleteAkim(id); messageApi.success('Удалено'); fetchAkims(); }
    catch (err: any) { messageApi.error(err?.response?.data?.detail || 'Ошибка'); }
  };

  // Vet handlers
  const handleVetSubmit = async () => {
    let values: any;
    try { values = await vetForm.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      if (editingVet) {
        await villageApi.updateVetService(editingVet.id, values);
        messageApi.success('Данные обновлены');
      } else {
        await villageApi.createVetService(values);
        messageApi.success('Служба добавлена');
      }
      setVetModalOpen(false);
      fetchVetServices();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVet = async (id: number) => {
    try { await villageApi.deleteVetService(id); messageApi.success('Удалено'); fetchVetServices(); }
    catch (err: any) { messageApi.error(err?.response?.data?.detail || 'Ошибка'); }
  };

  // Agro handlers
  const handleAgroSubmit = async () => {
    let values: any;
    try { values = await agroForm.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      if (editingAgro) {
        await villageApi.updateAgroInfo(editingAgro.id, values);
        messageApi.success('Данные обновлены');
      } else {
        await villageApi.createAgroInfo(values);
        messageApi.success('Информация добавлена');
      }
      setAgroModalOpen(false);
      fetchAgroInfo();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgro = async (id: number) => {
    try { await villageApi.deleteAgroInfo(id); messageApi.success('Удалено'); fetchAgroInfo(); }
    catch (err: any) { messageApi.error(err?.response?.data?.detail || 'Ошибка'); }
  };

  const akimColumns: ColumnsType<VillageAkim> = [
    { title: 'Село (рус.)', dataIndex: 'village_name_ru', key: 'village_name_ru', ellipsis: true, render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Село (каз.)', dataIndex: 'village_name_kk', key: 'village_name_kk', width: 160, ellipsis: true },
    { title: 'Аким', dataIndex: 'akim_name', key: 'akim_name', width: 180 },
    { title: 'Телефон', dataIndex: 'phone', key: 'phone', width: 140, render: (v: string) => v || <Text type="secondary">--</Text> },
    { title: 'Адрес', dataIndex: 'address', key: 'address', width: 180, ellipsis: true, render: (v: string) => v || <Text type="secondary">--</Text> },
    {
      title: 'Действия', key: 'actions', width: 100,
      render: (_: any, r: VillageAkim) => (
        <Space size={4}>
          <Tooltip title="Редактировать"><Button size="small" icon={<EditOutlined />} onClick={() => { setEditingAkim(r); akimForm.setFieldsValue(r); setAkimModalOpen(true); }} /></Tooltip>
          <Popconfirm title="Удалить?" onConfirm={() => handleDeleteAkim(r.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить"><Button size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const vetColumns: ColumnsType<VetService> = [
    { title: 'Название (рус.)', dataIndex: 'name_ru', key: 'name_ru', ellipsis: true, render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Атауы (каз.)', dataIndex: 'name_kk', key: 'name_kk', width: 180, ellipsis: true },
    { title: 'Тип услуги', dataIndex: 'service_type', key: 'service_type', width: 140, render: (v: string) => v || <Text type="secondary">--</Text> },
    { title: 'Телефон', dataIndex: 'phone', key: 'phone', width: 140, render: (v: string) => v || <Text type="secondary">--</Text> },
    { title: 'Адрес', dataIndex: 'address', key: 'address', width: 200, ellipsis: true, render: (v: string) => v || <Text type="secondary">--</Text> },
    {
      title: 'Действия', key: 'actions', width: 100,
      render: (_: any, r: VetService) => (
        <Space size={4}>
          <Tooltip title="Редактировать"><Button size="small" icon={<EditOutlined />} onClick={() => { setEditingVet(r); vetForm.setFieldsValue(r); setVetModalOpen(true); }} /></Tooltip>
          <Popconfirm title="Удалить?" onConfirm={() => handleDeleteVet(r.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить"><Button size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const agroColumns: ColumnsType<AgroInfo> = [
    { title: 'Заголовок', dataIndex: 'title', key: 'title', ellipsis: true, render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Категория', dataIndex: 'category', key: 'category', width: 140, render: (v: string) => v ? <Tag>{v}</Tag> : <Text type="secondary">--</Text> },
    { title: 'Дата', dataIndex: 'created_at', key: 'created_at', width: 120, render: (d: string) => dayjs(d).format('DD.MM.YYYY') },
    { title: 'Активно', dataIndex: 'is_active', key: 'is_active', width: 80, render: (v: boolean) => v ? <Tag color="green">Да</Tag> : <Tag color="red">Нет</Tag> },
    {
      title: 'Действия', key: 'actions', width: 100,
      render: (_: any, r: AgroInfo) => (
        <Space size={4}>
          <Tooltip title="Редактировать"><Button size="small" icon={<EditOutlined />} onClick={() => { setEditingAgro(r); agroForm.setFieldsValue(r); setAgroModalOpen(true); }} /></Tooltip>
          <Popconfirm title="Удалить?" onConfirm={() => handleDeleteAgro(r.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить"><Button size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getCreateButton = () => {
    if (activeTab === 'akims') return (
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingAkim(null); akimForm.resetFields(); setAkimModalOpen(true); }} style={{ background: 'var(--l-accent)' }}>
        Добавить акима
      </Button>
    );
    if (activeTab === 'vet') return (
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingVet(null); vetForm.resetFields(); setVetModalOpen(true); }} style={{ background: 'var(--l-accent)' }}>
        Добавить ветслужбу
      </Button>
    );
    return (
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingAgro(null); agroForm.resetFields(); setAgroModalOpen(true); }} style={{ background: 'var(--l-accent)' }}>
        Добавить запись
      </Button>
    );
  };

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Бурлинский район</Title>
          <Text type="secondary">Сельские акимы, ветслужбы и агроинформация</Text>
        </div>
        {getCreateButton()}
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Сельские акимы" value={akims.length} prefix={<UserOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Ветслужбы" value={vetServices.length} prefix={<MedicineBoxOutlined style={{ color: 'var(--l-ok)' }} />} valueStyle={{ color: 'var(--l-ok)' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Агроинфо" value={agroInfo.length} prefix={<FileTextOutlined style={{ color: 'var(--l-accent)' }} />} valueStyle={{ color: 'var(--l-accent)' }} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchAkims(); fetchVetServices(); fetchAgroInfo(); }}>Обновить</Button>}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ padding: '0 16px' }}
          items={[
            {
              key: 'akims', label: 'Сельские акимы',
              children: <Table<VillageAkim> rowKey="id" columns={akimColumns} dataSource={akims} loading={akimsLoading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Записи не найдены' }} scroll={{ x: 900 }} />,
            },
            {
              key: 'vet', label: 'Ветслужбы',
              children: <Table<VetService> rowKey="id" columns={vetColumns} dataSource={vetServices} loading={vetLoading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Записи не найдены' }} scroll={{ x: 900 }} />,
            },
            {
              key: 'agro', label: 'Агроинфо',
              children: <Table<AgroInfo> rowKey="id" columns={agroColumns} dataSource={agroInfo} loading={agroLoading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Записи не найдены' }} scroll={{ x: 700 }} />,
            },
          ]}
        />
      </Card>

      {/* Akim Modal */}
      <Modal title={editingAkim ? 'Редактировать акима' : 'Добавить акима'} open={akimModalOpen} onCancel={() => setAkimModalOpen(false)} onOk={handleAkimSubmit} confirmLoading={submitting} okText={editingAkim ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={600} destroyOnClose>
        <Form form={akimForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="village_name_ru" label="Село (рус.)" rules={[{ required: true, message: 'Введите название' }]}>
                <Input placeholder="Бестау" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="village_name_kk" label="Ауыл (каз.)" rules={[{ required: true, message: 'Атауын енгізіңіз' }]}>
                <Input placeholder="Бестау" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="akim_name" label="ФИО акима" rules={[{ required: true, message: 'Введите ФИО' }]}>
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Телефон">
                <Input placeholder="+7 711 33 2-XX-XX" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Адрес">
                <Input placeholder="с. Бестау, ул. Центральная, 1" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Vet Modal */}
      <Modal title={editingVet ? 'Редактировать ветслужбу' : 'Добавить ветслужбу'} open={vetModalOpen} onCancel={() => setVetModalOpen(false)} onOk={handleVetSubmit} confirmLoading={submitting} okText={editingVet ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={600} destroyOnClose>
        <Form form={vetForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name_ru" label="Название (рус.)" rules={[{ required: true, message: 'Введите название' }]}>
                <Input placeholder="Ветеринарная станция" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name_kk" label="Атауы (каз.)" rules={[{ required: true, message: 'Атауын енгізіңіз' }]}>
                <Input placeholder="Ветеринарлық бекет" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="service_type" label="Тип услуги">
            <Input placeholder="Вакцинация, осмотр, лечение" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Телефон">
                <Input placeholder="+7 711 33 2-XX-XX" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Адрес">
                <Input placeholder="г. Аксай, ул. ..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Agro Modal */}
      <Modal title={editingAgro ? 'Редактировать запись' : 'Добавить запись'} open={agroModalOpen} onCancel={() => setAgroModalOpen(false)} onOk={handleAgroSubmit} confirmLoading={submitting} okText={editingAgro ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={600} destroyOnClose>
        <Form form={agroForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Заголовок" rules={[{ required: true, message: 'Введите заголовок' }]}>
            <Input placeholder="Субсидии для фермеров 2026" />
          </Form.Item>
          <Form.Item name="body" label="Текст" rules={[{ required: true, message: 'Введите текст' }]}>
            <TextArea rows={4} placeholder="Подробная информация..." />
          </Form.Item>
          <Form.Item name="category" label="Категория">
            <Select placeholder="Выберите" allowClear>
              <Select.Option value="subsidy">Субсидии</Select.Option>
              <Select.Option value="season">Сезонные работы</Select.Option>
              <Select.Option value="market">Рынок</Select.Option>
              <Select.Option value="other">Другое</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VillagePage;
